import React, { useState } from "react";

const RFIForm = ({
  value,
  onChange,
  onSubmit,                 // legacy: receives event
  onSubmitWithAttachments,  // new: receives (payload, { documentFiles, photoFiles })
  onCancel,
  creating,
  projectMembers = [],
}) => {
  const [documentFiles, setDocumentFiles] = useState([]);
  const [photoFiles, setPhotoFiles] = useState([]);

  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (onSubmitWithAttachments) {
      // New behavior: send the RFI payload + selected files
      onSubmitWithAttachments(value, { documentFiles, photoFiles });
    } else if (onSubmit) {
      // Back-compat for existing ModalManager usage
      onSubmit(e);
    }
  };

  const handleDocsChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setDocumentFiles((prev) => [...prev, ...files]);
    // allow re-select same file
    e.target.value = "";
  };

  const handlePhotosChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPhotoFiles((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const removeDocAt = (idx) => {
    setDocumentFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const removePhotoAt = (idx) => {
    setPhotoFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <form
      onSubmit={handleFormSubmit}
      className="bg-white rounded-lg shadow p-6 space-y-4"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Subject */}
        <div>
          <label className="text-sm text-gray-700">Subject</label>
          <input
            value={value.subject}
            onChange={(e) => onChange("subject", e.target.value)}
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
            placeholder="Clarification on drawings"
            required
          />
        </div>

        {/* Priority */}
        <div>
          <label className="text-sm text-gray-700">Priority</label>
          <select
            value={value.priority}
            onChange={(e) => onChange("priority", e.target.value)}
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
          >
            <option value="low">low</option>
            <option value="medium">medium</option>
            <option value="high">high</option>
            <option value="urgent">urgent</option>
          </select>
        </div>

        {/* Required By Date */}
        <div>
          <label className="text-sm text-gray-700">Required By Date</label>
          <input
            type="date"
            value={value.required_by_date}
            onChange={(e) => onChange("required_by_date", e.target.value)}
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
          />
        </div>

        {/* Status */}
        <div>
          <label className="text-sm text-gray-700">Status</label>
          <select
            value={value.status}
            onChange={(e) => onChange("status", e.target.value)}
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
          >
            <option value="open">open</option>
            <option value="answered">answered</option>
            <option value="closed">closed</option>
          </select>
        </div>

        {/* Assignee – show name + role + email */}
        <div>
          <label className="text-sm text-gray-700">Assign To</label>
          <select
            value={value.assigned_to_user_id || ""}
            onChange={(e) =>
              onChange(
                "assigned_to_user_id",
                e.target.value ? Number(e.target.value) : null
              )
            }
            className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
          >
            <option value="">Unassigned</option>
            {projectMembers.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.first_name} {m.last_name}
                {m.role ? ` • ${m.role}` : ""}        {/* project role */}
                {m.email ? ` – ${m.email}` : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Question */}
      <div>
        <label className="text-sm text-gray-700">Question</label>
        <textarea
          value={value.question}
          onChange={(e) => onChange("question", e.target.value)}
          rows={4}
          className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
          placeholder="Describe the question clearly…"
          required
        />
      </div>

      {/* Attachments at creation time */}
      <div className="border-t pt-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Attachments</span>
          <span className="text-xs text-gray-400">
            Optional • Documents &amp; Photos
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Documents */}
          <div>
            <label className="block text-xs font-medium text-gray-600">
              Documents
            </label>
            <input
              type="file"
              multiple
              onChange={handleDocsChange}
              className="mt-1 block w-full text-xs text-gray-700
                         file:mr-3 file:px-3 file:py-1.5 file:rounded-md
                         file:border file:border-gray-300 file:bg-gray-50
                         file:text-gray-700 hover:file:bg-gray-100"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
            />
            {documentFiles.length > 0 && (
              <ul className="mt-2 space-y-1 max-h-24 overflow-y-auto text-xs">
                {documentFiles.map((f, idx) => (
                  <li
                    key={`${f.name}-${idx}`}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="truncate" title={f.name}>
                      {f.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeDocAt(idx)}
                      className="shrink-0 text-[11px] px-2 py-0.5 rounded
                                 bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Photos */}
          <div>
            <label className="block text-xs font-medium text-gray-600">
              Photos
            </label>
            <input
              type="file"
              multiple
              onChange={handlePhotosChange}
              className="mt-1 block w-full text-xs text-gray-700
                         file:mr-3 file:px-3 file:py-1.5 file:rounded-md
                         file:border file:border-gray-300 file:bg-gray-50
                         file:text-gray-700 hover:file:bg-gray-100"
              accept="image/*"
            />
            {photoFiles.length > 0 && (
              <ul className="mt-2 space-y-1 max-h-24 overflow-y-auto text-xs">
                {photoFiles.map((f, idx) => (
                  <li
                    key={`${f.name}-${idx}`}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="truncate" title={f.name}>
                      {f.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => removePhotoAt(idx)}
                      className="shrink-0 text-[11px] px-2 py-0.5 rounded
                                 bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={creating}
          className="px-4 py-2 rounded-md bg-gray-900 text-white text-sm font-medium hover:bg-black disabled:opacity-60"
        >
          {creating ? "Creating..." : "Create RFI"}
        </button>

        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-md bg-gray-100 text-gray-800 text-sm font-medium hover:bg-gray-200"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default RFIForm;